import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import {
  SELLER_CONNECT_QUESTION_REQUIREMENT,
  SellerConnectAdminSubmission,
} from 'models/SellerConnectEngine';
import { downloadUrlInNewWindow, getUserFullName } from 'utils';
import IntakeStatusTag from '../IntakeStatusTag';
import IntakeViaChip from '../IntakeViaChip';
import SellerDetailsCard from '../SellerDetailsCard';
import { SellersTableData } from '../SellersTable/data';
import {
  getSellerAnswerRows,
  getSellerAnswersByRequirement,
  getSellerDrawerDates,
  getSellerFormattedDate,
  getSellerIntakeDate,
  getSellerLatestImWithPdf,
  getHasSellerTranscript,
} from '../../utils';
import { EMPTY_CELL_VALUE } from 'data/global';
import SellerTranscript from '../SellerTranscript';

import './SellerDrawer.styles.scss';

type SellerDrawerProps = {
  className?: string;
  seller: SellersTableData;
} & SideDrawerProps;

const SellerDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  SellerDrawerProps
> = (props, ref) => {
  const { className, seller, closeAction, ...rest } = props;

  const { submissionId } = seller || {};

  const [submission, setSubmission] = useState<SellerConnectAdminSubmission>();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpeningIm, setIsOpeningIm] = useState(false);
  const [isRecordingShown, setIsRecordingShown] = useState(false);
  const [isTranscriptShown, setIsTranscriptShown] = useState(false);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const currentSubmissionId = useRef(submissionId);
  const isRecordingReloaded = useRef(false);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('seller-drawer', className);

  const { t } = useTranslation();

  const { popServerError } = useContext(PopNotificationsContext);

  const {
    seller: contact,
    business,
    screening,
    intakeStatus,
  } = submission || {};

  const hasImPdf = !!getSellerLatestImWithPdf(submission);
  const hasTranscript = getHasSellerTranscript(screening?.transcript);

  const contactRows = useMemo(
    () => [
      {
        label: t('name'),
        value: getUserFullName(contact?.firstName, contact?.lastName),
      },
      { label: t('email'), value: contact?.email || EMPTY_CELL_VALUE },
      { label: t('phone'), value: contact?.phone || EMPTY_CELL_VALUE },
    ],
    [contact, t],
  );

  const intakeRows = useMemo(
    () => [
      {
        label: t('sellerDrawerVia'),
        value: screening?.mode ? (
          <IntakeViaChip mode={screening.mode} />
        ) : (
          EMPTY_CELL_VALUE
        ),
      },
      {
        label: t('date'),
        value: getSellerFormattedDate(getSellerIntakeDate(submission)),
      },
    ],
    [screening, submission, t],
  );

  const businessRows = useMemo(
    () =>
      getSellerAnswerRows(
        getSellerAnswersByRequirement(
          submission,
          SELLER_CONNECT_QUESTION_REQUIREMENT.IM_DETAIL,
        ),
      ),
    [submission],
  );

  const optionalRows = useMemo(
    () =>
      getSellerAnswerRows(
        getSellerAnswersByRequirement(
          submission,
          SELLER_CONNECT_QUESTION_REQUIREMENT.OPTIONAL,
        ),
      ),
    [submission],
  );

  const internalRows = useMemo(
    () =>
      getSellerAnswerRows(
        getSellerAnswersByRequirement(
          submission,
          SELLER_CONNECT_QUESTION_REQUIREMENT.INTERNAL,
        ),
      ),
    [submission],
  );

  const openIm = useCallback(async () => {
    setIsOpeningIm(true);

    try {
      const {
        data: { url, type },
      } = await api.sellerConnectEngine.getSellerConnectAdminImPdf(
        submissionId,
      );

      if (url) downloadUrlInNewWindow(url, type);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsOpeningIm(false);
    }
  }, [popServerError, submissionId]);

  const closeDrawer = useCallback(() => {
    setIsRecordingShown(false);
    closeAction?.();
  }, [closeAction]);

  const fetchSubmission = useCallback(async () => {
    const { data } =
      await api.sellerConnectEngine.getSellerConnectAdminSubmission(
        submissionId,
      );

    if (currentSubmissionId.current === submissionId) setSubmission(data);
  }, [submissionId]);

  const reloadRecording = useCallback(() => {
    if (isRecordingReloaded.current) return;

    isRecordingReloaded.current = true;
    fetchSubmission();
  }, [fetchSubmission]);

  useEffect(() => {
    if (!submissionId) return;

    (async () => {
      try {
        setIsLoading(true);
        setSubmission(null);
        setIsRecordingShown(false);
        setIsTranscriptShown(false);
        currentSubmissionId.current = submissionId;
        isRecordingReloaded.current = false;

        await fetchSubmission();
      } catch (e) {
        popServerError(e);
      } finally {
        if (currentSubmissionId.current === submissionId) setIsLoading(false);
      }
    })();
  }, [fetchSubmission, popServerError, submissionId]);

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      title={seller?.seller}
      isLoading={isLoading}
      {...rest}
      closeAction={closeDrawer}
    >
      <header className="seller-drawer__header">
        <p className="seller-drawer__business">
          {business?.name || EMPTY_CELL_VALUE}
        </p>

        <div className="seller-drawer__meta">
          {intakeStatus && <IntakeStatusTag status={intakeStatus} />}
          {screening?.mode && <IntakeViaChip mode={screening.mode} />}
          <span className="seller-drawer__dates">
            {getSellerDrawerDates(submission, t)}
          </span>
          <Button
            className="seller-drawer__open-im"
            variant="outline"
            disabled={!hasImPdf || isOpeningIm}
            onClick={openIm}
          >
            {t('sellerDrawerOpenIm')}
          </Button>
        </div>
      </header>

      <div className="seller-drawer__cards">
        <SellerDetailsCard title={t('contact')} rows={contactRows} />

        <SellerDetailsCard title={t('sellerDrawerIntake')} rows={intakeRows}>
          {(screening?.recording || hasTranscript) && (
            <div className="seller-drawer__media-actions">
              {screening?.recording && (
                <Button
                  variant="outline"
                  onClick={() => setIsRecordingShown(!isRecordingShown)}
                >
                  {t(
                    isRecordingShown
                      ? 'sellerDrawerHideRecording'
                      : 'sellerDrawerPlayRecording',
                  )}
                </Button>
              )}
              {hasTranscript && (
                <Button
                  variant="outline"
                  onClick={() => setIsTranscriptShown(!isTranscriptShown)}
                >
                  {t(
                    isTranscriptShown
                      ? 'sellerDrawerHideTranscript'
                      : 'sellerDrawerViewTranscript',
                  )}
                </Button>
              )}
            </div>
          )}

          {isRecordingShown && screening?.recording && (
            <audio
              className="seller-drawer__recording"
              src={screening.recording.url}
              controls
              autoPlay
              onError={reloadRecording}
            />
          )}

          {isTranscriptShown && hasTranscript && (
            <SellerTranscript transcript={screening.transcript} />
          )}
        </SellerDetailsCard>

        <SellerDetailsCard
          title={t('sellerDrawerBusinessDetails')}
          rows={businessRows}
        />

        <SellerDetailsCard
          title={t('sellerDrawerOptionalDetails')}
          rows={optionalRows}
        />

        <SellerDetailsCard
          className="seller-drawer__internal"
          title={t('sellerDrawerInternalOnly')}
          note={t('sellerDrawerInternalOnlyNote')}
          rows={internalRows}
        />
      </div>
    </SideDrawer>
  );
};

export default forwardRef(SellerDrawer);
